import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { createPath, ROUTE } from "app-constants/routing"
import InfiniteHorizontalScroller from "components/InfiniteHorizontalScroller"
import FeaturedVideoCard from "components/videos/FeaturedVideoCard"
import { VideoPlayer } from "components/videos/VideoPlayer"
import useAuthRedirect from "hooks/useAuthRedirect"
import { api } from "lib/api"
import useCurrent from "lib/use-current"
import ConventionVideo from "models/convention-api/v1/ConventionVideo"
import React, { useState } from "react"

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      position: "relative",

      width: "100%",

      backgroundColor: theme.palette.primary.lightest,
    },
    scrollerContainer: {
      paddingTop: 30,
      paddingBottom: 40,
      zIndex: 1,
    },
    featuredVideoCardContainer: {
      marginRight: 30,
      [theme.breakpoints.up("sm")]: {
        minWidth: 504,
      },
    },
    background: {
      // we need these guys for full horizontal page background, since we use main-layout and content container it's not possible do it those those guys
      "&::before, &::after": {
        content: "''",
        position: "absolute",
        top: 0,
        bottom: 0,
        height: "100%",
        width: "100vw",
        backgroundColor: theme.palette.primary.lightest,
      },
      "&::before": {
        left: 0,
        marginLeft: "-100vw",
      },
      "&::after": {
        right: 0,
        marginRight: "-100vw",
      },
    },
  })
)

interface FeaturedVideosProps {
  fallbackData: ConventionVideo[]
}

export const FeaturedVideos = (props: FeaturedVideosProps) => {
  const { fallbackData } = props
  const current = useCurrent()
  const authRedirect = useAuthRedirect()
  const { classes } = useStyles()

  const [playingVideoURL, setPlayingVideoURL] = useState("")
  const videos = ConventionVideo.useMore({
    limit: 20,
    includes: ["institution", "video_count"],
    filter: {
      featured: "true",
    },
    fallbackData,
  })

  const onPlay = (video: ConventionVideo) => () =>
    authRedirect(async () => {
      // @ts-ignore
      if (current?.user) {
        try {
          await api.createVideoView({ req: undefined }, video.id)
        } catch (error) {
          console.error(error)
        }
      }
      setPlayingVideoURL(video.url)
    })

  return (
    <Box className={classes.container}>
      <Box className={classes.background} aria-hidden="true" />
      <Box className={classes.scrollerContainer}>
        <InfiniteHorizontalScroller
          title="Featured"
          emptyStateMessage="No videos found"
          hasMore={videos.hasMore}
          loadMore={videos.loadMore}
          loading={videos.isLoading}>
          {videos.data?.map((featuredVideo) => (
            <Box key={featuredVideo.id} className={classes.featuredVideoCardContainer}>
              <FeaturedVideoCard
                onPlay={onPlay(featuredVideo)}
                href={createPath({ path: ROUTE.VIDEO, params: { videoId: featuredVideo.id } })}
                imageSrc={featuredVideo.thumbnail_url ?? ""}
                imageAlt=""
                curatorName={featuredVideo.institution?.name ?? ""}
                title={featuredVideo.name}
                numberOfViews={featuredVideo.video_count?.video_count ?? 0}
              />
            </Box>
          ))}
        </InfiniteHorizontalScroller>
      </Box>
      <VideoPlayer
        videoURL={playingVideoURL}
        onClose={() => setPlayingVideoURL("")}
        isOpen={!!playingVideoURL}
      />
    </Box>
  )
}
